import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SignatureCanvas from '../components/SignatureCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LiabilityWaiverPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showFullWaiver, setShowFullWaiver] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [formData, setFormData] = useState({
    participantName: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    participantEmail: user?.email || '',
    phone: user?.phone || '',
    birthdate: '',
    emergencyContact: '',
    emergencyPhone: '',
    isMinor: false,
    guardianName: '',
    waiverAccepted: false,
    signatureName: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const handleSignatureChange = (imageData, typedName, mode) => {
    setSignatureMode(mode);
    setSignatureImage(imageData);
    setFormData(prev => ({
      ...prev,
      waiverAccepted: !!imageData,
      signatureName: typedName || prev.signatureName
    }));
  };

  const handleSubmit = async () => {
    if (!formData.participantName || !formData.birthdate) {
      toast.error('Please fill in your name and date of birth');
      return;
    }

    // Validate signature
    if (!signatureImage) {
      toast.error('Please provide your signature (draw or type your name)');
      return;
    }

    if (formData.isMinor && !formData.guardianName) {
      toast.error('Guardian name is required for minors');
      return;
    }

    try {
      await axios.post(`${API_URL}/liability-waivers`, {
        ...formData,
        signature_image: signatureImage,
        signature_type: signatureMode,
        submittedAt: new Date().toISOString(),
        userId: user?.id
      });
      
      setSubmitted(true);
      toast.success('Liability waiver submitted successfully!');
    } catch (error) {
      console.error('Error submitting waiver:', error);
      toast.success('Liability waiver completed! (Saved locally)');
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-12 bg-[#FAFAFA]">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold text-[#0A2342] mb-2">
              Liability Waiver Signed
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for completing the liability waiver. You're all set for diving activities with JustDive.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/trips')} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
                View Trips
              </Button>
              <Button variant="outline" onClick={() => navigate('/courses')}>
                View Courses
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0A2342] mb-4">
            Liability Waiver
          </h1>
          <p className="text-gray-600">
            Release and Waiver of Liability Agreement for diving activities.
          </p>
        </div>

        {/* Warning Banner */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <p className="text-sm text-orange-800 font-medium">Important Legal Document</p>
              <p className="text-xs text-orange-700 mt-1">
                Please read this waiver carefully before signing. By signing, you acknowledge the inherent risks of diving activities.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Participant Details */}
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-[#0A2342] mb-4">Participant Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Full Name *</label>
                <Input
                  value={formData.participantName}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantName: e.target.value }))}
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date of Birth *</label>
                <Input
                  type="date"
                  value={formData.birthdate}
                  onChange={(e) => setFormData(prev => ({ ...prev, birthdate: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.participantEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantEmail: e.target.value }))}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone Number</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Emergency Contact Name *</label>
                <Input
                  value={formData.emergencyContact}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContact: e.target.value }))}
                  placeholder="Name of emergency contact"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Emergency Contact Phone *</label>
                <Input
                  type="tel"
                  value={formData.emergencyPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyPhone: e.target.value }))}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
              <div className="flex items-center md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isMinor}
                    onChange={(e) => setFormData(prev => ({ ...prev, isMinor: e.target.checked }))}
                    className="w-4 h-4 text-[#0A2342] rounded"
                  />
                  <span className="text-sm text-gray-700">Participant is a minor (under 18)</span>
                </label>
              </div>
            </div>

            {formData.isMinor && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <label className="block text-sm text-yellow-800 mb-2">Parent/Guardian Name *</label>
                <Input
                  value={formData.guardianName}
                  onChange={(e) => setFormData(prev => ({ ...prev, guardianName: e.target.value }))}
                  placeholder="Enter parent/guardian full name"
                />
                <p className="text-xs text-yellow-700 mt-2">
                  As parent/guardian, you are signing on behalf of the minor and accepting responsibility.
                </p>
              </div>
            )}
          </div>

          {/* Waiver Content */}
          <div className="p-6 border-b">
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowFullWaiver(!showFullWaiver)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
              >
                <span className="font-medium text-[#0A2342]">Release and Waiver of Liability Agreement</span>
                {showFullWaiver ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${showFullWaiver ? 'max-h-[600px]' : 'max-h-48'}`}>
                <div className="p-4 bg-white text-sm text-gray-700 space-y-4 overflow-y-auto max-h-[580px]">
                  <div>
                    <h3 className="font-bold text-base mb-2">Declaration of Safe Diving Practices and Statement of Understanding</h3>
                    <p className="italic text-gray-600 mb-3">Subject to local laws and regulations</p>
                    
                    <p className="mb-3">As an ISC student and/or as a certified diver, I understand and I am aware of the principle that I should:</p>
                    
                    <ul className="list-disc pl-5 space-y-2 mb-4">
                      <li>Engage only in diving activities consistent with my training, certification, experience, and conditions consistent with my training and avoid diving beyond my limits without proper training.</li>
                      <li>Maintain good physical fitness and mental health for diving.</li>
                      <li>Avoid being under the influence of dangerous drugs or alcohol when diving.</li>
                      <li>Keep proficient in diving skills, reviewing them in controlled conditions after 6 (six) months of diving inactivity. I shall refer to my course materials to stay current and refresh myself on important information.</li>
                      <li>When diving new sites, obtain a formal diving orientation from a knowledgeable, local source.</li>
                      <li>I shall refrain from diving if I feel uncomfortable if there is a problem discovered following the equipment assembly and checks and it cannot be fixed in time before the dive.</li>
                      <li>Maintain equipment and follow manufacturer's guidelines for service, maintenance, and replacement consumables.</li>
                      <li>Deny use of my SCUBA equipment to uncertified divers and only dive on/with equipment that is consistent with my training.</li>
                      <li>Respect and follow the advice of those supervising my diving activities.</li>
                      <li>Plan dives within reasonable safe limits, including but not limited to: communications, emergency procedures, local dive laws and regulations, etc.</li>
                      <li>Avoid decompression diving unless trained and certified to do so.</li>
                      <li>Monitoring no-decompression limit, dive time/s and gas pressures/volume and close dive buddy contact.</li>
                      <li>Never breath-hold or skip breaths when breathing compressed gas and avoid excessive hyperventilation when breath-hold diving.</li>
                      <li>Use surface support.</li>
                    </ul>
                    
                    <p className="mb-4">I understand the importance and reasoning of the safe diving practices listed above. I will follow them to the best of my abilities. I understand that my failure to follow them may place me and others in danger and may result in death(s) when diving. I exonerate any liability that may have incurred because of my failure to follow these practices and that may be stated in the Release and Waiver of Liability Agreement.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-base mb-2">Release and Waiver of Liability Agreement</h3>
                    
                    <p className="mb-3">Scuba diving, Freediving and Snorkeling has inherent risks that the participant assumes. ISC training professionals conduct these activities under strict standards.</p>
                    
                    <p className="mb-3">In consideration of the risk by participating in activities through the facility known as International Scuba Certification ISC (hereinafter referred to as the "Dive Center"), I hereby accept responsibility for myself and for my heirs, administrators, successors, and assigns.</p>
                    
                    <p className="mb-3">I hereby release, remiss, and discharge <strong>JustDive, Scuba Crew; DIVECC PTY LTD T/as International Scuba Certification (ISC)</strong>, their respective, affiliates, shareholders, employees, facilities, instructors, and affiliated personnel, and its instructors, through which I receive instruction; all vessels (whether owned, operated, leased, or chartered); and all other involved personnel including, but not limited to, the owners, employees, crew, volunteers, designee's, agents, sponsors, and advertisers (hereinafter referred to as the "Released Parties"); and all other participants in those activities of and from all claims, demands, actions, causes of action of any sort, arising out of law or in equity for injury sustained to myself or my property during my presence and/or participation in activities.</p>
                    
                    <p className="mb-3">I understand the purpose of accepting this statement and hereby affirm that I am aware that these activities have inherent risks that may result in serious injury or death.</p>
                    
                    <p className="mb-3">I acknowledge that freediving; snorkeling; and/or diving with compressed air, oxygen-enriched gas, involves certain inherent risks that may cause injury or even death. General risks that may affect a range of underwater activities may include, but are not limited to, drowning, pressure related injuries, equipment failure, decompression sickness, narcosis, perils of the sea, or acts of a fellow diver. Freediving / Snorkeling may present a risk of shallow water blackout. Specialty diving may involve deeper or more complex diving, including diving in overhead environments.</p>
                    
                    <p className="mb-3">I acknowledge that practical training, either in confined and/or open water, is necessary for certification and that the training may be conducted at a location that is remote, from a recompression chamber and/or professional medical services. I agree to proceed with the diving activities despite my being fully aware of the proximity of medical assistance and the possible absence of a recompression chamber in proximity to the dive site and/or the possible delay in receiving professional medical care or attention. I assume all these risks. I hereby waive any obligation on the part of the Released Parties to provide me with first aid, rescue, recovery resuscitation, oxygen administration or medical assistance.</p>
                    
                    <p className="mb-3">I understand rescue may involve unacceptable risk for the rescuer or may not be successful in all circumstances. In the event a rescue is attempted and fails, I understand and accept that no claim may be made against Parties directly or indirectly involved, subject of this agreement.</p>
                    
                    <p className="mb-3">I intend by this release to waive all claims for negligence, products liability, or breach of warranty against the Dive Center, including claims for personal injury to myself or for damage to my property, whether it is based on the sole negligence of the Dive Center, its agents, or its employees. This release shall cover and include all areas, activities and acts, and rental and/or use of equipment, if applicable, at the Dive Center and the other locations of instruction including, but not limited to, all recreational endeavors, parking facilities, picnicking areas, land, showers, restrooms, and office in or about the Dive Center or connected with the same.</p>
                    
                    <p className="mb-3">I acknowledge that the utilization of the facility known as the Dive Center and the other locations of instruction by me for whatever purposes is with my knowledge and permission and is purely at my risk. I agree that there have been no warranties made, either expressed or implied concerning such utilization.</p>
                    
                    <p className="mb-3">I acknowledge that there are no lifeguards at the facility known as the Dive Center and the other locations of instruction, and that by participating in the diving activities, I assume all risks.</p>
                    
                    <p className="mb-3">I agree to indemnify and hold harmless the Dive Center or its parent corporation from all causes of action at law or in equity that I and further on behalf of any personal representative, heir, executor, administrator, or successor may have for my decision if I decide not take Insurance coverage for the activities that I am about to take part.</p>
                    
                    <p className="mb-3">I agree to indemnify and hold harmless the Dive Center or its parent corporation from all causes of action at law or in equity that I and further on behalf of any personal representative, heir, executor, administrator, or successor may have. I agree to pay all costs of such action including, but not limited to, attorney's fees incurred by the Dive Center or any parent corporation in the defense of same.</p>
                    
                    <p className="mb-3">I acknowledge that all equipment I will be using and or responsible for, must be inspected prior to use, including, but not limited to, gas mixtures, gas supply, and consumables prior to any diving activity. I will notify the Released Parties if any of the equipment is not working properly or outside parameters. I will not hold the Released Parties responsible for any failure by me to inspect the equipment prior to any diving activity or any consequential injury and/or death because of my knowledge of equipment failure or failure to inspect.</p>
                    
                    <p className="mb-3">I certify that (1) the digital signature on this release is voluntary; (2) I am the person for whom the release is signed; and (3) I have had the opportunity to inspect the facility and the other locations of instruction, am satisfied with the condition of the facility and the other locations of instruction, am aware of any hazards that may exist thereon, and have assumed all risks associated therewith.</p>
                    
                    <p className="mb-3">I agree that this release shall be continuing in nature for subsequent visits during the calendar year set forth below.</p>
                    
                    <p className="mb-3">I acknowledge that if any part of this waiver is unenforceable, it does not waive the entire agreement or that which is in part or full within the local laws and regulations.</p>
                    
                    <p className="font-bold mb-3">I HAVE READ AND UNDERSTOOD THE FOREGOING RELEASE, AND BY AFFIXING MY DIGITAL SIGNATURE TO IT, I SIGNIFY MY CLEAR INTENTION TO BE LEGALLY BOUND BY IT.</p>
                    
                    <p className="mb-3">IF THE PARTICIPANT IS A LEGAL MINOR AS DEFINED BY LOCAL LAWS AND REGULATIONS, I, THE UNDERSIGNED PARENT OR GUARDIAN, HAVE READ AND UNDERSTOOD THE FOREGOING RELEASE. I HEREBY ACCEPT RESPONSIBILITY FOR THE MINOR AND FOR THE CHILD'S HEIRS, ADMINISTRATORS, SUCCESSORS, AND ASSIGNS. BY AFFIXING MY DIGITAL SIGNATURE TO THE RELEASE, I SIGNIFY MY CLEAR INTENTION TO BE LEGALLY BOUND BY IT.</p>
                    
                    <p className="font-bold">THIS AGREEMENT, AS PUBLISHED, SHALL NOT BE AMENDED OR MODIFIED OR ANY OF ITS PROVISIONS WAIVED, UNLESS IN WRITING AND SIGNED BY THE DULY AUTHORIZED REPRESENTATIVES OF BOTH PARTIES.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {!showFullWaiver && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click above to expand and read the full waiver
              </p>
            )}
          </div>

          {/* Signature Section */}
          <div className="p-6 bg-gray-50">
            <h2 className="text-lg font-semibold text-[#0A2342] mb-4">Digital Signature</h2>

            <div className="bg-white border rounded-lg p-4 space-y-4">
              {/* Signature Canvas - Draw or Type */}
              <SignatureCanvas 
                onSignatureChange={handleSignatureChange}
                width={400}
                height={120}
                label={formData.isMinor ? "Parent/Guardian Signature" : "Your Signature"}
                initialTypedName={formData.signatureName}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Signature Date *</label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, signatureDate: e.target.value }))}
                  />
                </div>
              </div>
              
              <label className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100">
                <input
                  type="checkbox"
                  checked={formData.waiverAccepted && !!signatureImage}
                  disabled
                  className="mt-1 w-5 h-5 text-[#FF6B35] rounded"
                />
                <div>
                  <span className="text-sm font-semibold text-orange-800">
                    {signatureImage ? (
                      <span className="text-green-700">Signature captured - I accept the Release and Liability Waiver</span>
                    ) : (
                      'I have read and accept the Release and Liability Waiver *'
                    )}
                  </span>
                  <p className="text-xs text-orange-700 mt-1">
                    {signatureImage 
                      ? 'Your signature has been captured. By signing above, you confirm this constitutes your legal electronic signature.'
                      : 'Please sign above (draw or type your name) to accept the waiver.'
                    }
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSubmit}
                className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 px-8"
                data-testid="submit-waiver-form"
              >
                Sign and Submit Waiver
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiabilityWaiverPage;
